import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { addMinutesToUtcDate } from 'src/utilities/addMinutesToUtcDate';
import {
  ExpiresOn,
  TimeIntervalTypeValues,
} from 'src/components/oneCorp/features/MealCredit/common/formFields/mealCreditFormFields.utils';
import {
  IMealCreditFormFields,
  Meridian,
  TIME_FORMAT_SHORT,
  TIME_MERIDIAN_FORMAT,
} from 'src/components/oneCorp/features/MealCredit/utils/EnterpriseManageMealCreditsPage.utils';
import { getFirstInstanceDateUTC } from 'src/models/LineOfCreditModelUtils';
import { calculateTimeIntervalType } from 'src/components/oneCorp/features/MealCredit/utils/calculateTimeIntervalType';
import { getDailyPeriodStartFromEndDay } from 'src/components/oneCorp/features/MealCredit/utils/getDailyPeriodStartFromEndDay';

export interface CalculateExpirationFieldsResult {
  expirationField: Date | null;
  expirationDate: Date | null;
  expiresOn: ExpiresOn;
}

export const calculateDateFields = ({
  firstInstanceUtc,
  durationMinutes,
}: {
  firstInstanceUtc: string;
  durationMinutes: number;
}): Pick<
  IMealCreditFormFields,
  | 'startTime'
  | 'startTimeMeridian'
  | 'endTime'
  | 'endTimeMeridian'
  | 'timeIntervalType'
  | 'date'
> => {
  const firstStartDate = getFirstInstanceDateUTC(firstInstanceUtc);
  const firstEndDate = addMinutesToUtcDate(firstInstanceUtc, durationMinutes);

  const startTime = format(firstStartDate, TIME_FORMAT_SHORT);
  const startTimeMeridian =
    Meridian[format(firstStartDate, TIME_MERIDIAN_FORMAT)];
  const endTime = format(firstEndDate, TIME_FORMAT_SHORT);
  const endTimeMeridian = Meridian[format(firstEndDate, TIME_MERIDIAN_FORMAT)];

  const timeIntervalType = calculateTimeIntervalType({
    startTime,
    startTimeMeridian,
    endTime,
    endTimeMeridian,
  });

  return {
    date: firstStartDate,
    startTime,
    startTimeMeridian,
    endTime,
    endTimeMeridian,
    timeIntervalType,
  };
};

export const calculateExpirationFields = ({
  expiration,
  timeIntervalType,
}: {
  expiration: string | null;
  timeIntervalType: TimeIntervalTypeValues;
}): CalculateExpirationFieldsResult => {
  if (!expiration) {
    return {
      expirationField: null,
      expirationDate: null,
      expiresOn: ExpiresOn.NEVER,
    };
  }

  const expirationDate = utcToZonedTime(expiration, 'UTC');

  // For overnight credits: Expiration form field shows final instance START date instead of expiration date
  // Example: Overnight credit from Aug 31 5PM to Sept 1 10AM form field Expiration shows Aug 31
  // We use expiration form field as the final instance start date and expirationDate as the effective expiration date
  const expirationField = getDailyPeriodStartFromEndDay(
    expirationDate,
    timeIntervalType
  );

  return {
    expirationField,
    expirationDate,
    expiresOn: ExpiresOn.ON_THIS_DAY,
  };
};
